import { clearLocationCache, getLocationWithCache } from './location.lib';
import { enrichLocationWithGeodata } from './geocoding.lib';
import type {
  OneSignalChangeEvent,
  OneSignalSDK
} from './constants/onesignal-types.lib';

const handleLogout = async (OneSignal: OneSignalSDK): Promise<void> => {
  try {
    await OneSignal.logout();
    clearLocationCache();
  } catch {}
};

const addEmailIfExists = async (
  OneSignal: OneSignalSDK,
  email?: string
): Promise<void> => {
  if (!email) {
    return;
  }

  try {
    await OneSignal.User.addEmail(email);
  } catch {}
};

const linkIfLogged = async (OneSignal: OneSignalSDK): Promise<void> => {
  const session = window.__userSession as
    | { id: string; email?: string }
    | undefined;

  if (!session?.id) {
    await handleLogout(OneSignal);
    return;
  }

  await OneSignal.login(String(session.id));
  await addEmailIfExists(OneSignal, session.email);
};

const addLocationTags = async (OneSignal: OneSignalSDK): Promise<void> => {
  try {
    logWarn('Starting location tag process...');

    const location = await getLocationWithCache();

    if (!location) {
      logWarn('Could not obtain location');
      return;
    }

    logWarn('Location obtained:', {
      lat: location.latitude.toFixed(4),
      lng: location.longitude.toFixed(4)
    });

    const enrichedLocation = await enrichLocationWithGeodata(location);

    if (enrichedLocation.state && enrichedLocation.region) {
      await OneSignal.User.addTags({
        state: enrichedLocation.state,
        region: enrichedLocation.region
      });

      logWarn('Location tags added successfully:', {
        state: enrichedLocation.state,
        region: enrichedLocation.region
      });
    } else {
      logWarn('Could not determine state/region from location');
    }
  } catch (error) {
    logWarn('Failed to add location tags:', error);
  }
};

export const shouldLoadOneSignal = (env: PublicEnvVars): boolean => {
  const hasValidAppId = Boolean(env.PUBLIC_ONESIGNAL_APP_ID);
  const isProduction = env.PUBLIC_APP_ENV === 'production';
  const isDevEnabled = env.PUBLIC_ENABLE_ONESIGNAL_DEV === 'true';
  return hasValidAppId && (isProduction || isDevEnabled);
};

const logWarn = (...args: unknown[]) => {
  try {
    // eslint-disable-next-line no-console
    console.warn('[OneSignal]', ...args);
  } catch {}
};

export const setupOneSignal = async (env: PublicEnvVars): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!shouldLoadOneSignal(env)) {
    return;
  }

  const run = async (OneSignal: OneSignalSDK): Promise<void> => {
    if (window.__onesignalSetupDone) {
      return;
    }
    window.__onesignalSetupDone = true;

    try {
      await getLocationWithCache();
      logWarn('Location permission requested on OneSignal setup');
    } catch (error) {
      logWarn('Failed to get initial location:', error);
    }

    await OneSignal.init({
      appId: env.PUBLIC_ONESIGNAL_APP_ID,
      serviceWorkerParam: {
        scope: '/push/onesignal/',
        workerPath: '/push/onesignal/OneSignalSDKWorker.js'
      },
      allowLocalhostAsSecureOrigin: env.PUBLIC_APP_ENV === 'development'
    });

    await linkIfLogged(OneSignal);

    try {
      OneSignal.User.PushSubscription.addEventListener(
        'change',
        async (e: OneSignalChangeEvent) => {
          if (
            (e && e.current && e.current.optedIn) ||
            e?.state === 'subscribed'
          ) {
            await linkIfLogged(OneSignal);

            await addLocationTags(OneSignal);
          }
        }
      );
    } catch {}

    window.addEventListener('user:login', () => {
      linkIfLogged(OneSignal).catch(error =>
        logWarn('login link failed', error)
      );
    });
    window.addEventListener('user:logout', () => {
      OneSignal.logout().catch((error: unknown) =>
        logWarn('logout failed', error)
      );
      clearLocationCache();
    });
  };

  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(run);

  // If SDK already present, run immediately as well
  if (window.OneSignal && typeof window.OneSignal.init === 'function') {
    try {
      await run(window.OneSignal);
    } catch (error) {
      if (env.PUBLIC_APP_ENV === 'development') {
        logWarn('setup failed', error);
      }
    }
  }
};
